import React from 'react';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Box, Divider, Typography } from '@mui/material';

export interface AppointmentTypesSetupSignalsProps {
  /** How many appointment types are available to the practice. */
  count: number;
}

const Signal: React.FC<{
  icon: React.ReactElement;
  title: string;
  body: string;
}> = ({ icon, title, body }) => (
  <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0 }}>
    <Box sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {body}
      </Typography>
    </Box>
  </Box>
);

/**
 * "Current practice setup" overview for the Appointment Types section: the
 * count of configured types plus three signals explaining what an appointment
 * type controls (scheduling, charting, follow-through). Moved here from the
 * retired standalone practice page; rendered inside the section card's top
 * subsection.
 */
export const AppointmentTypesSetupSignals: React.FC<AppointmentTypesSetupSignalsProps> = ({
  count,
}) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <EventAvailableIcon color="primary" />
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Current practice setup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {count === 1
            ? '1 appointment type is available to the practice.'
            : `${count} appointment types are available to the practice.`}
        </Typography>
      </Box>
    </Box>

    <Divider sx={{ my: 2 }} />

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
        gap: 2,
      }}
    >
      <Signal
        icon={<ScheduleIcon />}
        title="Scheduling"
        body="Name, color, duration, and visit mode."
      />
      <Signal
        icon={<ArticleIcon />}
        title="Charting"
        body="The note template that should open for each visit type."
      />
      <Signal
        icon={<AssignmentTurnedInIcon />}
        title="Follow-through"
        body="Intake forms and appointment reminders."
      />
    </Box>
  </Box>
);
